/**
 * CaptainReportGate.jsx — THE CAPTAIN'S DAILY REPORT (2026-09-11, Matt's idea; docs/CAPTAIN_REPORT.md).
 *
 * "The captain of any milestone has to submit a report at the same time as they have to submit their
 * time cards." Once a day, for each plane in the air that this person CAPTAINS (never a co-pilot's
 * plane), four quick asks — about thirty seconds a plane, two planes at most:
 *   1. Where is it on the flight path?      the same curve the board draws, with the plane dragged along
 *                                           it — the ONLY place a plane gets moved by its own crew;
 *   2. Days of YOUR OWN hands-on work left? a number, not a date, and never waiting time — or the honest
 *                                           answer "none, I am waiting on someone else", which hands the
 *                                           plane to the tower (round two, 2026-09-16);
 *   3. Is anything in the way?             one pick, so the tower and the Drum get a real signal;
 *   4. One line for the tower.             required only if the plane did not move.
 *
 * Same shape as the other gates: waits for the member + seat to resolve, fails OPEN when the server
 * can't be read, defers to the Time Gate (time comes first), announces itself with
 * `report-gate-blocking-changed` so the Triage Gate defers to this one. NO SNOOZE (Matt, 2026-09-16):
 * the card shows when Cortex opens and stays until the report is sent — "the lockout shouldn't happen
 * halfway through the day", and it can't, because nothing pushes it there any more.
 *
 * "Today" is the captain's own day: the server computes it in the timezone on their roster row
 * (Admin → Crew), so a crew member hours ahead of Laguna Beach is asked once per THEIR day.
 *
 * Round one additions (2026-09-14): a WALKTHROUGH the first time, step by step over the real form;
 * a CONFIRM when the plane was not moved. Round two (2026-09-16): the curve replaces the straight
 * slider; the waiting answer; the tower's ASKS card (below) for the people who owe direction.
 *
 * ASKS FOR THE TOWER: after their own reports, a tower seat (workload/admin) sees every open "need
 * direction from Matt or Brandon" with the captain's note, and answers each — Handled, or Will do on a
 * day. The captain reads the answer on the plane's hover card and on their next report card.
 *
 * Mounted once in OutlineView, after <TimeGate/> and before <TriageGate/>.
 */

import RoleChip from './RoleChip.jsx'
import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { getCrewFocus, ackCrewFocus, getCaptainReportsDue, submitCaptainReport, getReviewAsks, getCoordinatorRequests, requestAction, getHandoffRetired, getReviewWindow, getTriageDraft, saveTriageDraft, clearTriageDraft } from '../services/flights.js'
import { getHandoffMembers } from '../services/members.js'
import { EntityPeekModal } from './EntityPeek.jsx'
import TriageTiles, { TileIcon } from './TriageTiles.jsx'
import { hill, attitudeFor, Plane, PlaneDefs } from './FlightTracker.jsx'
import Select from './Select.jsx'
import { lockScroll } from '../outline/scrollLock.js'
import { getMyInstructions, answerInstruction, postReadiness } from '../services/alderflow.js'
import { alertDialog } from './Dialog.jsx'
import { activateProjectHold } from '../services/projectHold.js'
import { startDirectMessage } from '../services/slack.js'

// What the crew can ask for on the card (2026-10-15). Each one says who picks it up; never a name.
const REQ_CATS = [
  { value: 'client', pick: 'The client', label: 'From the client',             who: 'the Client Coordinator chases it' },
  { value: 'consultant', pick: 'A consultant', label: 'From a consultant',           who: 'the Consultant Coordinator chases it' },
  { value: 'agency', pick: 'The city or an agency', label: 'From the city or an agency',  who: 'the Permitting Coordinator chases it' },
  { value: 'review', pick: 'A review or design direction', label: 'A review or design direction', who: 'the review and direction roles call it' },
  { value: 'other', pick: 'Something else', label: 'Something else',              who: 'the review and direction roles call it' },
]
const REQ_LABEL = Object.fromEntries(REQ_CATS.map(c => [c.value, c]))
// Kinds a coordinator chases; for these the captain may say "I'm chasing this myself" (2026-10-22).
const COORD_CATS = new Set(['client', 'consultant', 'agency'])
// Where an open request stands, in the crew's words.
function reqStatus(r) {
  if (r.status === 'answered') return `Answered by ${r.answeredBy || 'a reviewer'}: ${r.answer}`
  if (r.status === 'received') return 'Received'
  if (r.status === 'sent_back') return `Sent back: ${r.sentBackNote || 'needs more detail'}`
  if (r.status === 'claimed') return `${r.claimedBy || 'A reviewer'} called it · in their queue`
  if (r.status === 'chased') return `${r.chasedBy || 'The coordinator'} is on it · checking back ${fmtDay(r.expectedDate)}`
  if (r.calledTo) return `${r.calledBy || 'Someone'} called it to ${r.calledTo} · waiting for them to take it`
  if (r.escalated) return 'Escalated to review and direction · waiting for someone to call it'
  return r.route === 'review' ? 'Waiting for a reviewer to call it' : 'Waiting for the coordinator'
}
// THE REVIEW WINDOW (2026-10-16): your own time and queue, beside the asks, so you can tell whether
// you are the best one to take it. Your calendar only; nobody sees anyone else's.
const tFmt = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(':00', '').replace(' ', '').toLowerCase()
const dFmt = (iso) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString()
const hrs = (m) => m >= 60 ? (Math.round(m / 30) / 2) + ' h' : m + ' min'
const PRI = { finished: 0, midflight: 1 }
function landsLine(a, win) {
  const q = win.queue || []
  const ahead = q.filter(x => (PRI[x.tag] ?? 1) < (PRI[a.tag] ?? 1) || ((PRI[x.tag] ?? 1) === (PRI[a.tag] ?? 1) && ((x.risk || 0) > (a.risk || 0) || ((x.risk || 0) === (a.risk || 0) && String(x.createdAt) <= String(a.createdAt))))).length
  const b = (win.blocks || []).find(x => x.freeMins >= 15)
  const pos = ahead === 0 ? 'First in your queue' : `#${ahead + 1} in your queue, behind ${ahead}`
  return 'If you take it: ' + pos + (b ? ` · your next block is ${isToday(b.start) ? 'today' : dFmt(b.start)} ${tFmt(b.start)}` : '')
}
function ReviewWindow({ win }) {
  if (!win) return <div style={{ ...S.win, color: '#8aa6b3' }}>Looking at your calendar…</div>
  const q = win.queue || []
  return (
    <div style={S.win}>
      <div style={S.winCol}>
        <div style={S.winHead}>Your time</div>
        {win.calendar !== 'ok' ? (
          <div style={S.winLine}>Connect your Google Calendar on your time card to see your free time here.</div>
        ) : (
          <>
            <div style={S.winLine}><b>Today:</b> {win.todayFree.length ? win.todayFree.slice(0, 3).map(g => `${tFmt(g.start)}–${tFmt(g.end)}`).join(', ') : 'nothing free'}</div>
            {win.blocks.length ? win.blocks.slice(0, 3).map(b => (
              <div key={b.start} style={S.winLine}><b>{isToday(b.start) ? 'Today' : dFmt(b.start)}</b> block {tFmt(b.start)}–{tFmt(b.end)}{b.freeMins < b.mins ? <span style={{ color: '#b8540a' }}> · {b.freeMins ? hrs(b.freeMins) + ' still free' : 'booked over'}</span> : ''}</div>
            )) : <div style={S.winLine}>No review or direction blocks on your calendar in the next ten days.</div>}
          </>
        )}
      </div>
      <div style={S.winCol}>
        <div style={S.winHead}>Already in your queue · {q.length || 'empty'}</div>
        {q.length ? q.slice(0, 4).map((x, i) => (
          <div key={x.id} style={S.winLine}>{i + 1}. {x.flightNumber} {x.projectName}{x.tag === 'finished' ? ' · finished, waiting to post' : ''}</div>
        )) : <div style={S.winLine}>Nothing taken yet. Whatever you take starts your next block.</div>}
        {q.length > 4 && <div style={S.winLine}>and {q.length - 4} more</div>}
      </div>
    </div>
  )
}
const daysSince = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000))

const BLOCKERS = [
  { value: 'none',       label: 'Nothing — it is moving' },
  { value: 'client',     label: 'Waiting on the client' },
  { value: 'consultant', label: 'Waiting on a consultant' },
  { value: 'agency',     label: 'Waiting on the agency' },
  { value: 'direction',  label: 'Need direction from Matt or Brandon' },
  { value: 'passenger',  label: 'Need a decision on a passenger' },
  { value: 'other',      label: 'Something else — say what below' },
]

// The same five readings the flight curve uses (FlightTracker.jsx#hillRead), kept in step by hand.
function hillRead(t) {
  if (t < 0.18) return 'Wheels up'
  if (t < 0.42) return 'Plotting the course'
  if (t < 0.58) return 'Course set'
  if (t < 0.85) return 'Flying the course'
  return 'On final approach'
}
const fmtDay = (s) => s ? new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'
const todayYmd = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
const plusDays = (ymd, n) => { const d = new Date(ymd + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10) }

// The walkthrough runs once per browser. v3: the curve, the waiting answer, no snooze.
const TOUR_KEY = 'aj-captain-report-tour:v4'
function readTourSeen() { try { return localStorage.getItem(TOUR_KEY) === '1' } catch { return false } }

// One entry per thing on the form, in reading order. `target` names the ref the callout points at.
const TOUR_STEPS = [
  {
    target: 'head', title: 'This is the milestone you are reporting on',
    body: 'You are its captain, so once a day, when you open Cortex, this card asks about it and stays until you answer. If you captain two milestones you get one card, then the next. Thirty seconds each.',
  },
  {
    target: 'hill', title: 'Drag the plane to where the work really stands',
    body: 'This is the same flight path the Flight Board draws. The climb is plotting the course: still working out how to do it. The descent is flying the course: you know how, now it is about getting it done. Move the plane every day you made progress. If you leave it where it was, that tells the tower nothing moved today, and we will ask you to confirm before sending.',
  },
  {
    target: 'blocker', title: 'What are you still waiting to receive?',
    body: 'Your open requests carry over from earlier days, with where each one stands. Tick "We got it" when one arrives. Add anything new you need and do not have yet, even if you still have plenty of other work: asking early puts it in someone\'s queue sooner. Each request goes to whoever handles that kind: a coordinator chases clients, consultants and agencies; the review and direction roles take reviews and direction.',
  },
  {
    target: 'days', title: 'Can you keep working right now?',
    body: 'A separate question from what you are waiting for. Yes means you have your own work to do while you wait, and then the days of your own hands-on work left: never waiting time. No means you are stopped until someone else acts. The scheduler uses the days overnight to set the finish date.',
  },
  {
    target: 'note', title: 'One sentence for the tower',
    body: 'What changed, or why it did not. Optional when you moved the plane. Required when you did not, because the tower needs to know why.',
  },
  {
    target: 'foot', title: 'Send it',
    body: 'Send report saves your answers, moves the milestone on the Flight Board, feeds tonight\'s schedule, and posts a short summary to the project\'s Slack channel. There is no later. The card stays until it is sent.',
  },
]

/**
 * THE FLIGHT PATH (2026-09-16, Matt: "change the straight line bar to a curve mimicking the flight
 * curve so it's more tightly related"). The same `hill()` the board draws, with the plane on it at the
 * captain's chosen spot, its nose following the slope exactly as it does on the board. Drag anywhere on
 * the path; arrow keys nudge by one percent (the invisible range input underneath keeps keyboard and
 * screen-reader access).
 */
function FlightPathSlider({ value, onChange, disabled }) {
  const W = 560, H = 132, PAD = 26, BASE = 108, AMP = 82
  const ref = useRef(null)
  const x = (t) => PAD + t * (W - 2 * PAD)
  const y = (t) => BASE - hill(t) * AMP
  const pts = []
  for (let i = 0; i <= 80; i++) { const t = i / 80; pts.push(`${x(t).toFixed(1)},${y(t).toFixed(1)}`) }
  const path = 'M' + pts.join(' L')
  const t = Math.max(0, Math.min(1, value / 100))
  const px = x(t), py = y(t)

  function fromEvent(ev) {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const rel = (ev.clientX - r.left) / r.width * W
    const nt = Math.max(0, Math.min(1, (rel - PAD) / (W - 2 * PAD)))
    onChange(Math.round(nt * 100))
  }
  function onPointerDown(ev) {
    if (disabled) return
    ev.preventDefault()
    fromEvent(ev)
    const move = (e) => fromEvent(e)
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* The plane glyph is a shared <symbol>; the board mounts it, but this card can show up on any
          page, so it carries its own copy. */}
      <PlaneDefs />
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', cursor: disabled ? 'default' : 'grab', touchAction: 'none', userSelect: 'none' }}
        onPointerDown={onPointerDown} aria-hidden="true">
        {/* ground, the path, and the part already flown */}
        <line x1={PAD} y1={BASE + 6} x2={W - PAD} y2={BASE + 6} stroke="rgba(7,39,45,0.18)" strokeWidth="1" />
        <path d={path} fill="none" stroke="rgba(7,39,45,0.28)" strokeWidth="1.5" strokeDasharray="4 5" />
        <path d={path} fill="none" stroke="#07272D" strokeWidth="2.5" strokeLinecap="round"
          pathLength="100" strokeDasharray={`${Math.max(0.01, t * 100)} 100`} />
        <line x1={x(0.5)} y1={y(0.5) - 10} x2={x(0.5)} y2={BASE + 6} stroke="rgba(7,39,45,0.12)" strokeWidth="1" strokeDasharray="2 4" />
        <text x={x(0.5)} y={y(0.5) - 15} textAnchor="middle" fontFamily="Montserrat, sans-serif" fontSize="8.5" letterSpacing="1.2" fill="#8aa6b3">COURSE SET</text>
        <text x={x(0)} y={H - 2} textAnchor="start" fontFamily="Montserrat, sans-serif" fontSize="8.5" letterSpacing="1.2" fill="#8aa6b3">PLOTTING THE COURSE</text>
        <text x={x(1)} y={H - 2} textAnchor="end" fontFamily="Montserrat, sans-serif" fontSize="8.5" letterSpacing="1.2" fill="#8aa6b3">FLYING THE COURSE</text>
        <circle cx={px} cy={py} r="15" fill="rgba(184,84,10,0.10)" />
      </svg>
      <div style={{ position: 'absolute', left: `${(px / W) * 100}%`, top: `${(py / H) * 100}%`, transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
        <Plane deg={attitudeFor(t)} color="#07272D" size={26} spin />
      </div>
      <input type="range" min="0" max="100" step="1" value={value} disabled={disabled} aria-label="Where the work stands on the flight path, 0 to 100"
        onChange={e => onChange(Number(e.target.value))}
        style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', opacity: 0, pointerEvents: 'none' }} />
    </div>
  )
}

// Today's half-made report, kept in this browser too (2026-10-29), so it never depends on one server read.
const DRAFT_FIELDS = ['hill01', 'days', 'waiting', 'blocker', 'keep', 'newReqs', 'gotIt', 'dropReq', 'resendText', 'selfChase', 'note']
const draftSig = (o) => JSON.stringify(DRAFT_FIELDS.map(k => o[k] === undefined ? null : o[k]))
const localDraftKey = (memberId, flightId, day) => `aj-captain-draft:${memberId}:${flightId}:${day}`
function readLocalDraft(key) { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null } catch { return null } }
function writeLocalDraft(key, v) { try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* private window: the server copy still saves */ } }
function clearLocalDraft(key) { try { localStorage.removeItem(key) } catch { /* fine */ } }

export default function CaptainReportGate() {
  const [status, setStatus] = useState(null)               // { today, flights } | null while unknown
  // THE MORNING CREW CARD (Matt, 2026-10-27): your planes today, acknowledged once a day, before anything else.
  const [focus, setFocus] = useState(null)                 // { today, acked, flights } | null while unknown
  const [timeGateBlocking, setTimeGateBlocking] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [hill01, setHill01] = useState(8)                  // 0..100
  const [days, setDays] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [blocker, setBlocker] = useState('none')
  const [note, setNote] = useState('')
  const [confirmNoMove, setConfirmNoMove] = useState(false)
  const [tour, setTour] = useState(() => (readTourSeen() ? null : 0))   // null = not running, else step index
  const [callout, setCallout] = useState(null)             // { top, left, width, above }
  const [placeTick, setPlaceTick] = useState(0)            // re-tries placing the walkthrough note
  // ASKS FOR THE TOWER: null = not loaded, [] = none. Skipped hides them for this page load only.
  // The crew's requests on this report (2026-10-15): new ones, ticked "We got it", withdrawn, and
  // sent-back ones clarified for resending. keep = can the crew keep working ('yes' | 'no' | null).
  const [keep, setKeep] = useState(null)
  const [newReqs, setNewReqs] = useState([])               // [{ category, text, selfChase?, expectedDate? }]
  const [selfChase, setSelfChase] = useState({})          // carried request id -> expected date ('' until picked)
  const [draftSelf, setDraftSelf] = useState(false)
  const [draftDate, setDraftDate] = useState('')
  const [draftCat, setDraftCat] = useState('client')
  const [draftText, setDraftText] = useState('')
  const [reqErr, setReqErr] = useState('')
  const draftInput = useRef(null)
  const [gotIt, setGotIt] = useState({})                   // requestId -> true
  const [dropReq, setDropReq] = useState({})               // requestId -> true (withdraw a sent-back one)
  const [resendText, setResendText] = useState({})         // requestId -> clarified text
  // The two receiving cards: null = not loaded, [] = none.
  const [asks, setAsks] = useState(null)                   // review and direction roles (and balls called to me)
  const [coordReqs, setCoordReqs] = useState(null)         // my coordinator role(s)
  // INSTRUCTIONS FROM THE OPERATIONS DIRECTOR TRIAGE (2026-10-22): "Post ICP101, back-date it...".
  const [instrs, setInstrs] = useState(null)
  const [team, setTeam] = useState([])
  const [youPick, setYouPick] = useState({})               // requestId -> memberId
  const [chaseDate, setChaseDate] = useState({})           // requestId -> ymd
  const [peek, setPeek] = useState(null)
  const [win, setWin] = useState(null)                     // the review window: undefined/null = loading             // reportId -> ymd for "Will do on"
  const pollRef = useRef(null)
  const draftBase = useRef(null)      // the form as restored; unchanged from it = nothing to save
  const [resumedReport, setResumedReport] = useState(false)
  const refs = { head: useRef(null), hill: useRef(null), days: useRef(null), blocker: useRef(null), note: useRef(null), foot: useRef(null) }
  const cardRef = useRef(null)

  useEffect(() => {
    function onTimeGate(e) { setTimeGateBlocking(!!(e.detail && e.detail.blocking)) }
    window.addEventListener('time-gate-blocking-changed', onTimeGate)
    // Posted from the report (or anywhere): that plane has landed, so it owes no report now.
    const onPosted = (e) => { const d = e.detail || {}; if (d.posted && d.msId) setStatus(s => s ? { ...s, flights: (s.flights || []).filter(f => f.milestoneId !== d.msId) } : s) }
    window.addEventListener('post-ms-modal-closed', onPosted)
    return () => { window.removeEventListener('time-gate-blocking-changed', onTimeGate); window.removeEventListener('post-ms-modal-closed', onPosted) }
  }, [])

  const memberId = ctx.currentMember && ctx.currentMember.id
  const memberName = ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Name'] || ctx.currentMember.fields['Member Name']) || null
  const isTower = isSeatLoaded() && (hasModule('workload') || hasModule('admin'))
  const due = (status && Array.isArray(status.flights)) ? status.flights : []
  const current = due[0] || null
  const [postAskDone, setPostAskDone] = useState({})   // flightId -> answered "presented / done?" this session
  const [postReady, setPostReady] = useState({})       // milestoneId -> postReadiness()
  useEffect(() => {
    const id = current && current.milestoneId
    if (!id || postReady[id] || !(current.meetingAsk || current.nearDone)) return
    postReadiness(id).then(r => setPostReady(m => ({ ...m, [id]: r }))).catch(() => {})
  }, [current && current.milestoneId])   // eslint-disable-line react-hooks/exhaustive-deps
  const today = (status && status.today) || todayYmd()
  const openAsks = (asks || []).filter(a => !a.done)
  const openCoord = (coordReqs || []).filter(a => !a.done)
  const openInstr = (instrs || []).filter(a => !a.done)
  const showInstr = openInstr.length > 0
  // No "Not now" (2026-10-15): every ask and request gets an answer before the card lets you through.
  const showAsks = due.length === 0 && openAsks.length > 0
  const showCoord = due.length === 0 && openAsks.length === 0 && openCoord.length > 0
  const showFocus = !!(focus && !focus.acked && focus.flights && focus.flights.length > 0)
  const blocking = !isEmbedded() && timeGateBlocking === false && (showFocus || showInstr || due.length > 0 || showAsks || showCoord)

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('report-gate-blocking-changed', { detail: { blocking, known: status !== null && timeGateBlocking !== null } }))
  }, [blocking, status, timeGateBlocking])

  const recheck = useCallback(async () => {
    const mid = ctx.currentMember && ctx.currentMember.id
    if (!isSeatLoaded() || !mid) return
    try { setFocus(await getCrewFocus(mid)) } catch (_) { setFocus(prev => prev || { acked: true, flights: [] }) }   // fail open
    try {
      const s = await getCaptainReportsDue(mid)
      setStatus({ today: s.today, flights: Array.isArray(s.flights) ? s.flights : [] })
    } catch (_) {
      setStatus({ today: todayYmd(), flights: [] })   // fail OPEN — a report gate must never lock anyone out on a server hiccup
    }
    // Asks reach the review and direction roles; anyone may have a ball called to them, so everyone
    // asks (the server returns only called-to-you balls to non-tower seats). Fail open on errors.
    // A failed REFRESH keeps what is on screen (2026-10-22: Jennie's requests card vanished when she came back to
    // the tab with nothing answered, the request still open). Only a first load with nothing yet fails open.
    try { setAsks(await getReviewAsks(mid)) } catch (_) { setAsks(prev => prev || []) }
    try { setCoordReqs(await getCoordinatorRequests(mid)) } catch (_) { setCoordReqs(prev => prev || []) }
    try { setInstrs(await getMyInstructions(mid)) } catch (_) { setInstrs(prev => prev || []) }
  }, [])
  useEffect(() => {
    if (!showAsks) return
    let alive = true
    getReviewWindow(memberId).then(w => { if (alive) setWin(w) }).catch(() => { if (alive) setWin({ calendar: 'none', todayFree: [], blocks: [], queue: [] }) })
    return () => { alive = false }
  }, [showAsks, openAsks.length])
  useEffect(() => {
    if (!(showAsks || showCoord) || team.length) return
    Promise.all([getHandoffMembers(), getHandoffRetired().catch(() => [])])
      .then(([t, gone]) => { const g = new Set(gone); setTeam((t || []).filter(m => m.name && !g.has(m.id))) })
      .catch(() => setTeam([]))
  }, [showAsks, showCoord])

  // Mount-time wait for the member + seat (they resolve after the outline boots), then a focus recheck
  // and a slow poll so a captain who leaves the tab open still gets asked the next morning.
  useEffect(() => {
    let alive = true, tries = 0
    const tick = async () => {
      if (!alive) return
      if (isSeatLoaded() && ctx.currentMember && ctx.currentMember.id) { await recheck(); return }
      if (++tries < 120) setTimeout(tick, 500)
    }
    tick()
    const onFocus = () => recheck()
    window.addEventListener('focus', onFocus)
    pollRef.current = setInterval(recheck, 15 * 60 * 1000)
    return () => { alive = false; window.removeEventListener('focus', onFocus); if (pollRef.current) clearInterval(pollRef.current) }
  }, [recheck])

  // A fresh form for each plane as it comes to the front.
  // Then today's half-made answers for this plane, if any (a reload, or another computer).
  // 2026-10-29 (Matt: "added items don't stay in the list"): the server copy alone was not enough.
  // When its read failed or came back late, the form sat on blank defaults and a request the captain
  // had added was gone from the screen while still saved. This browser's own copy now restores at
  // once, and the server copy is applied only when it is newer than anything made here. A form nobody
  // has touched is never saved, so a blank form can't overwrite a real draft.
  useEffect(() => {
    if (!current) return
    let base = {
      hill01: Math.round((current.hillPosition == null ? 0.08 : Number(current.hillPosition)) * 100),
      days: current.daysRemaining == null || current.waitingSince ? '' : String(current.daysRemaining),
      waiting: !!current.waitingSince, blocker: 'none',
      keep: current.waitingSince ? 'no' : (current.daysRemaining > 0 ? 'yes' : null),
      newReqs: [], gotIt: {}, dropReq: {}, resendText: {}, selfChase: {}, note: '',
    }
    const apply = (d, resumed) => {
      base = { ...base }
      for (const k of DRAFT_FIELDS) if (d[k] !== undefined && d[k] !== null) base[k] = d[k]
      setHill01(base.hill01); setDays(base.days); setWaiting(base.waiting); setBlocker(base.blocker); setKeep(base.keep)
      setNewReqs(Array.isArray(base.newReqs) ? base.newReqs : []); setGotIt(base.gotIt); setDropReq(base.dropReq)
      setResendText(base.resendText); setSelfChase(base.selfChase); setNote(base.note)
      draftBase.current = draftSig(base)
      setResumedReport(resumed)
    }
    setDraftText(''); setDraftSelf(false); setDraftDate(''); setReqErr(''); setErr(''); setConfirmNoMove(false)
    const lkey = localDraftKey(memberId, current.flightId, today)
    const local = readLocalDraft(lkey)
    apply(local || {}, !!local)
    let alive = true
    getTriageDraft(memberId, 'captain-report', current.flightId, today).then(d => {
      if (!alive || !d) return
      const mine = readLocalDraft(lkey)   // read again: the captain may have typed while this was on its way
      if (mine && (mine.savedAt || 0) >= (d.savedAt || 1)) return
      apply(d, true)
    }).catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current && current.flightId])
  // Save the answers as they are made, so the report survives a reload or a switch of computer:
  // this browser at once, the server a moment later.
  useEffect(() => {
    if (!current) return
    const content = { hill01, days, waiting, blocker, keep, newReqs, gotIt, dropReq, resendText, selfChase, note }
    if (draftSig(content) === draftBase.current) return
    const payload = { ...content, savedAt: Date.now() }
    writeLocalDraft(localDraftKey(memberId, current.flightId, today), payload)
    const t = setTimeout(() => {
      saveTriageDraft(memberId, 'captain-report', current.flightId, today, payload).catch(() => {})
    }, 600)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hill01, days, waiting, blocker, keep, newReqs, gotIt, dropReq, resendText, selfChase, note])

  useEffect(() => {
    if (!blocking) return
    return lockScroll()   // shared counter: the page scrolls again once every lockout has closed
  }, [blocking])

  // Place the walkthrough callout under (or over) the step's target, and keep it there through
  // scrolling and resizing. Measured, not guessed: the card scrolls and the form wraps at narrow widths.
  const stepDef = tour == null ? null : TOUR_STEPS[tour]
  useLayoutEffect(() => {
    // Not while another card holds the window (the morning crew card, 2026-10-28: Rafael's walkthrough
    // froze, its note never placed because the report wasn't on screen yet when this ran).
    if (!blocking || !current || !stepDef || showFocus || showInstr) { setCallout(null); return }
    const el = refs[stepDef.target].current
    if (!el) { if (placeTick > 120) return; const id = requestAnimationFrame(() => setPlaceTick(n => n + 1)); return () => cancelAnimationFrame(id) }   // its target isn't drawn yet: try next frame
    el.scrollIntoView({ block: 'nearest' })
    const place = () => {
      const r = el.getBoundingClientRect()
      const w = Math.min(360, window.innerWidth - 32)
      const roomBelow = window.innerHeight - r.bottom
      const above = roomBelow < 190 && r.top > 190
      const left = Math.max(16, Math.min(r.left, window.innerWidth - w - 16))
      setCallout({ width: w, left, top: above ? r.top - 12 : r.bottom + 12, above })
    }
    place()
    const card = cardRef.current
    window.addEventListener('resize', place)
    if (card) card.addEventListener('scroll', place)
    return () => { window.removeEventListener('resize', place); if (card) card.removeEventListener('scroll', place) }
  }, [blocking, tour, current && current.flightId, showFocus, showInstr, placeTick])

  if (!blocking) return null

  // ── THE RECEIVING CARDS (2026-10-15) ──────────────────────────────────────────────────────────
  async function act(item, setList, fields) {
    setBusy(true); setErr('')
    try {
      await requestAction({ requestId: item.id, memberId, memberName, ...fields })
      setList(prev => (prev || []).map(x => x.id === item.id ? { ...x, done: true } : x))
    } catch (e) { setErr(String(e && e.message || e)) } finally { setBusy(false) }
  }
  // The tiles (TriageTiles.jsx) do the asking; this does the work and returns the words the row
  // answers back with. The row folds away on its own a moment later (fold()).
  async function run(item, fields) {
    setErr('')
    await requestAction({ requestId: item.id, memberId, memberName, ...fields })
    window.dispatchEvent(new CustomEvent('crew-index-changed'))
  }
  const fold = (item, setList) => () => setList(prev => (prev || []).map(x => x.id === item.id ? { ...x, done: true } : x))
  const others = team.filter(t => t.id !== memberId)
  const nameOf = (id) => (team.find(t => t.id === id) || {}).name || 'them'
  const first = (n) => String(n || '').split(' ')[0]
  const openBtn = (a, label) => (
    <button type="button" style={S.openLink} onClick={() => setPeek({ type: 'milestone', entityId: a.milestoneId, projectId: a.projectId, projectName: a.projectName })}>
      <TileIcon name="open" size={15} />{label}
    </button>
  )
  function askOptions(a) {
    const captain = first(a.createdBy) || 'the captain'
    const lands = win ? landsLine(a, win).replace(/^If you take it: /, '') : 'Goes into your review queue in priority order.'
    return [
      { key: 'mine', icon: 'mine', label: 'Mine', sub: 'I take it', tone: 'primary',
        step: { title: 'Take it yourself', body: `${lands}. ${captain} sees that you have it.`, confirm: () => 'Take it', confirmIcon: 'mine' },
        run: () => run(a, { action: 'mine' }), done: () => `Yours. ${lands}.` },
      { key: 'you', icon: 'handoff', label: 'You', sub: 'Call it to someone else',
        step: { kind: 'person', people: others, title: 'Call it to someone', body: 'They get a Slack message and it is on their Triage today. If they do not take it today, it drops back into everyone\'s Triage tomorrow.', confirm: (v) => v ? `Call it to ${first(nameOf(v))}` : 'Pick someone', confirmIcon: 'handoff' },
        run: (v) => run(a, { action: 'you', toMemberId: v, toName: nameOf(v) }), done: (v) => `Called to ${nameOf(v)}. They have until tonight to take it.` },
      { key: 'answer', icon: 'answer', label: 'Answer now', sub: 'Quick enough to settle here',
        step: { kind: 'text', title: 'Answer it now', body: `Your answer goes straight to ${captain} and closes the ask.`, placeholder: 'Looks good, post it.', confirm: () => 'Send the answer', confirmIcon: 'answer' },
        run: (v) => run(a, { action: 'answer', text: v }), done: () => `Answered. ${captain} has it.` },
      { key: 'back', icon: 'sendback', label: 'Send back', sub: 'Not ready to review', tone: 'caution',
        step: { kind: 'text', title: 'Send it back', body: `Say what is missing or unclear. ${captain} gets a Slack message and sees it on their next report.`, placeholder: 'Add the topo survey before review.', confirm: () => `Send back to ${captain}`, confirmIcon: 'sendback' },
        run: (v) => run(a, { action: 'sendBack', text: v }), done: () => `Sent back to ${captain}.` },
    ]
  }
  // THE REQUESTS CARD, reworked 2026-10-29 (Matt: "something ambiguous about what I am being asked to
  // do", and "calling whatever the submitter needs 'it' is confusing" — captains send things to get,
  // questions, meetings to set up, and plain status notes). So: one question, worded for any kind of
  // request; three answers (Handled / I'm on it / Stuck); the rarer exits in a quiet row; and talking to
  // the captain is a real Slack DM (talkToCaptain), not a bot message whose reply nobody sees.
  function coordOptions(a) {
    const captain = first(a.createdBy) || 'the captain'
    const question = a.calledToYou ? `${first(a.calledBy) || 'Someone'} handed this to you. Where does it stand?`
      : a.overdue ? `You were checking back on this ${fmtDay(a.expectedDate)}. Where does it stand?`
      : 'Where does this stand?'
    const main = []
    if (a.calledToYou) main.push({ key: 'mine', icon: 'mine', label: 'Mine', sub: 'I’ll take care of this', tone: 'primary',
      step: { title: 'Take it yourself', body: `It becomes yours. ${captain} sees who has it.`, confirm: () => 'Take it', confirmIcon: 'mine' },
      run: () => run(a, { action: 'mine' }), done: () => 'Yours.' })
    main.push(
      { key: 'came', icon: 'cameback', label: 'Handled', sub: `${captain} has what they need`, tone: !a.calledToYou && a.overdue ? 'primary' : undefined,
        step: { title: 'Handled', body: `Closes the request and tells ${captain} on Slack that they aren’t waiting on it any more.`, confirm: () => 'Close it', confirmIcon: 'cameback' },
        run: () => run(a, { action: 'cameBack' }), done: () => `Closed. ${captain} has been told.` },
      { key: 'chased', icon: 'chased', label: a.overdue ? 'Still on it' : 'I’m on it', sub: 'Pick a day to check back', tone: !a.calledToYou && !a.overdue ? 'primary' : undefined,
        step: { kind: 'date', min: today, defaultValue: plusDays(today, 3), title: a.overdue ? 'Still on it: when do you check back?' : 'On it: when do you check back?', body: `It leaves your list until that day, and comes back then only if it isn’t handled. ${captain} sees you have it and the day.`, confirm: (v) => `Check back ${fmtDay(v)}`, confirmIcon: 'chased' },
        run: (v) => run(a, { action: 'chased', expectedDate: v }), done: (v) => `On it. Back on your list ${fmtDay(v)} if it isn’t handled by then.` },
      // Stuck: the two ways a request outgrows the coordinator. Project on hold (Matt, 2026-10-27): a
      // captain reporting "the client paused the project" had no way out of this lockout, since the hold
      // lives on the project's ⋯ menu, which the card covers. Same call as PutProjectOnHoldModal.
      { key: 'stuck', icon: 'stuck', label: 'Stuck', sub: 'Needs a principal, or the client paused', tone: 'caution',
        step: { kind: 'custom', defaultValue: { mode: 'esc', text: '' }, title: 'Stuck: what kind?',
          body: 'Escalate when it needs a principal to step in. Put the project on hold when the client has paused the work.',
          render: (v, set) => (
            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {[['esc', 'Escalate to a principal'], ['hold', `Put ${a.projectName || 'the project'} on hold`]].map(([m, l]) => (
                  <label key={m} style={S.checkRow}><input type="radio" name={'stuck-' + a.id} checked={v.mode === m} onChange={() => set({ ...v, mode: m })} />{l}</label>
                ))}
              </div>
              {v.mode === 'hold' && <div style={{ ...S.hint, marginBottom: 8 }}>Billing, progress updates and scheduling stop until someone resumes it. The client is not emailed. The team gets a Slack post now and every 30 days, and the Client Manager is reminded every 30 days to check in personally. {captain} is released from the plane and its requests are withdrawn, this one included.</div>}
              <textarea className="aj-tt-text" value={v.text} onChange={e => set({ ...v, text: e.target.value })}
                placeholder={v.mode === 'esc' ? 'Why? e.g. the client has not answered two follow-ups' : 'Internal note (optional, Slack only, never shown to the client)'} />
            </div>
          ),
          ready: (v) => v.mode === 'hold' || !!String(v.text || '').trim(),
          confirm: (v) => v.mode === 'hold' ? 'Put it on hold' : 'Escalate it', confirmIcon: 'escalate' },
        run: async (v) => {
          if (v.mode !== 'hold') return run(a, { action: 'escalate', text: String(v.text || '').trim() })
          const r = await activateProjectHold(a.projectId, { reason: String(v.text || '').trim() || a.text || null })
          if (r && r.error && !/already/i.test(r.error)) throw new Error(r.error)
          if (ctx.projectHoldIds) ctx.projectHoldIds.add(a.projectId)
          window.dispatchEvent(new CustomEvent('crew-index-changed'))
        },
        done: (v) => v.mode === 'hold' ? `On hold. ${captain} is released from the plane and the team has been told in Slack.` : 'Escalated to review and direction.' },
    )
    const more = [
      { key: 'notreq', icon: 'notreq', label: 'Not a request',
        step: { kind: 'text', optional: true, title: 'Not a request: nothing to chase', body: `Closes it. ${captain} gets a Slack message saying so, and to add it on their next report if they do need something.`, placeholder: `A word to ${captain} (optional)`, confirm: () => 'Close it', confirmIcon: 'notreq' },
        run: (v) => run(a, { action: 'notRequest', text: v || '' }), done: () => `Closed. ${captain} has been told.` },
      { key: 'you', icon: 'handoff', label: 'Hand off',
        step: { kind: 'person', people: others, title: 'Hand it to someone', body: 'They get a Slack message. If they do not take it today, it drops back to the usual coordinator tomorrow.', confirm: (v) => v ? `Hand it to ${first(nameOf(v))}` : 'Pick someone', confirmIcon: 'handoff' },
        run: (v) => run(a, { action: 'you', toMemberId: v, toName: nameOf(v) }), done: (v) => `Handed to ${nameOf(v)}.` },
      { key: 'back', icon: 'sendback', label: 'Send back to reword',
        step: { kind: 'text', title: 'Send it back', body: `Say what is unclear. ${captain} rewords it on their next report and it comes back to you.`, placeholder: 'Which consultant, and for which sheet?', confirm: () => `Send back to ${captain}`, confirmIcon: 'sendback' },
        run: (v) => run(a, { action: 'sendBack', text: v }), done: () => `Sent back to ${captain}.` },
    ]
    return { question, main, more }
  }
  // TALK TO THE CAPTAIN (2026-10-29): a real DM from the coordinator's own Slack, seeded with the
  // request (Time Review's speech-bubble pattern), then recorded on the request. The row stays.
  async function talkToCaptain(a) {
    const seed = `About your request on ${a.flightNumber || 'your milestone'}${a.projectName ? ' · ' + a.projectName : ''}: “${a.text}”. `
    setErr('')
    try {
      const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
      const res = await startDirectMessage({ submitterMemberId: a.createdById, text: seed }, token)
      if (res && res.permalink) window.open(res.permalink, '_blank', 'noopener')
      else if (res && res.deepLink) {
        if (res.needsConnect) { try { await navigator.clipboard.writeText(seed) } catch (_) { /* fine */ } }
        window.open(res.deepLink, '_blank', 'noopener')
      } else { alertDialog({ title: 'Could not open Slack', message: (res && res.needsSlackId) ? `No Slack account is on file for ${a.createdBy || 'the captain'}. Add it on their contact, or message them in Slack directly.` : ((res && res.error) || 'Could not start a Slack message.') }); return }
      requestAction({ requestId: a.id, memberId, memberName, action: 'talked' }).catch(() => {})
      setCoordReqs(prev => (prev || []).map(x => x.id === a.id ? { ...x, contextAskedAt: new Date().toISOString(), talkedHere: true } : x))
    } catch (e) { setErr(String(e && e.message || e)) }
  }
  const tiles = (a, opts, setList) => {
    const { main, more = [], question } = Array.isArray(opts) ? { main: opts } : opts
    return (
      <TriageTiles options={main} more={more} question={question} disabled={busy}
        onConfirm={async (key, v) => { const o = main.find(x => x.key === key) || more.find(x => x.key === key); await o.run(v); return o.done(v) }}
        onFolded={fold(a, setList)} />
    )
  }
  const histLine = (item) => {
    const h = (item.history || []).filter(x => /YOU|dropped|escalated|sent back|resent/.test(x.what)).slice(-2)
    return h.length ? ' · ' + h.map(x => (x.by ? x.by + ': ' : '') + x.what).join(' · ') : ''
  }

  // THE INSTRUCTION CARD comes first: it is about one of your planes, and often settles it.
  function instrOptions(a) {
    const from = first(a.fromName) || 'the Operations Director'
    const answer = (ans, note) => answerInstruction(memberId, memberName, a.id, ans, note)
    if (a.kind === 'note') return [
      { key: 'ok', icon: 'did', label: 'Got it', sub: 'Thanks', tone: 'primary', step: { title: 'Got it', body: 'The note folds away.', confirm: () => 'Got it', confirmIcon: 'did' }, run: () => answer('posted'), done: () => 'Got it.' },
    ]
    if (a.kind !== 'post') return [
      { key: 'done', icon: 'did', label: 'Done', sub: 'I did it', tone: 'primary', step: { title: 'Done', body: `${from} sees it is done.`, confirm: () => 'Done', confirmIcon: 'did' }, run: () => answer('posted'), done: () => 'Done.' },
      { key: 'cant', icon: 'sendback', label: 'Can’t yet', sub: 'Say why', tone: 'caution', step: { kind: 'text', title: 'Why not yet?', body: `${from} gets your answer on Slack.`, placeholder: 'One line', confirm: () => `Send to ${from}`, confirmIcon: 'sendback' }, run: (v) => answer('cant', v), done: () => `Sent to ${from}.` },
    ]
    return [
      { key: 'posting', icon: 'check', label: 'Posting it now', sub: 'Opens the post window', tone: 'primary',
        step: { title: `Post ${a.flightNumber || 'it'}`, body: 'The post window opens with this instruction beside the date.', confirm: () => 'Open the post window', confirmIcon: 'check' },
        run: async () => { await answer('posting'); window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: a.milestoneId, zIndex: 1000006, instruction: a.text } })) },
        done: () => 'The post window is open.' },
      { key: 'posted', icon: 'did', label: 'Already posted', sub: 'It is done',
        step: { title: 'Already posted', body: `${from} sees it is done.`, confirm: () => 'Already posted', confirmIcon: 'did' }, run: () => answer('posted'), done: () => 'Noted.' },
      { key: 'cant', icon: 'sendback', label: 'Can’t yet', sub: 'Say why', tone: 'caution',
        step: { kind: 'text', title: 'Why can’t it post yet?', body: `${from} gets your answer on Slack.`, placeholder: 'One line', confirm: () => `Send to ${from}`, confirmIcon: 'sendback' },
        run: (v) => answer('cant', v), done: () => `Sent to ${from}.` },
    ]
  }
  // THE MORNING CREW CARD: read, then Acknowledge. No links into the outline (Matt: "That's useless"); after it,
  // a captain goes straight on to their captain's reports.
  if (showFocus) {
    const fl = focus.flights
    const ack = async () => {
      setBusy(true); setErr('')
      try {
        await ackCrewFocus({ memberId, memberName, flights: fl.map(f => ({ flightNumber: f.flightNumber, projectName: f.projectName, milestoneName: f.milestoneName, priority: f.priority, isCaptain: f.isCaptain })) })
        setFocus(x => ({ ...x, acked: true }))
      } catch (e) { setErr(e.message || 'Could not save. Try again.') }
      finally { setBusy(false) }
    }
    const pri = (f) => f.parked ? 'Parked' : f.priority === 1 ? 'First priority' : f.priority === 2 ? 'Second priority' : 'Crewed'
    return (
      <div style={S.scrim} role="dialog" aria-modal="true" aria-label="Your planes today">
        <div style={S.card} ref={cardRef}>
          <RoleChip role="Crew" what="What you are crewed on today" />
          <div style={S.eyebrow}>Good morning{memberName ? ', ' + String(memberName).split(' ')[0] : ''}</div>
          <h2 style={S.title}>{fl.length === 1 ? 'Your plane today.' : `Your ${fl.length} planes today.`}</h2>
          <hr style={S.rule} />
          {fl.map(f => {
            const late = f.due && f.due < (focus.today || todayYmd())
            const partner = (f.with || []).map(w => w.name.split(' ')[0]).join(' and ')
            return (
              <div key={f.flightId} style={{ ...S.ask, opacity: f.parked ? 0.6 : 1 }}>
                <div style={S.askHead}>
                  <span style={S.askNo}>{f.flightNumber}</span>
                  <span style={S.askTitle}>{pri(f)}{f.isCaptain ? ' · you are captain' : ''}</span>
                </div>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 15, fontWeight: 600, color: '#07272D' }}>{f.projectName}</div>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, color: '#3a5260' }}>{f.milestoneName}</div>
                <div style={{ ...S.hint, fontSize: 12, marginTop: 3 }}>
                  {partner ? 'With ' + partner : 'Flying it alone'}
                  {f.hill != null && ' · ' + Math.round(f.hill * 100) + '% along the flight path'}
                  {f.due && <span style={{ color: late ? '#c0392b' : undefined }}> · due {fmtDay(f.due)}</span>}
                  {f.holding && <span> · holding: {f.holding}</span>}
                </div>
              </div>
            )
          })}
          {err && <div style={S.err}>{err}</div>}
          <div style={{ ...S.foot, marginTop: 14 }}>
            <span style={S.footNote}>{due.length ? 'Next: your captain’s report.' : 'That’s your day.'}</span>
            <button type="button" style={{ ...S.primary, opacity: busy ? 0.45 : 1 }} disabled={busy} onClick={ack}>{busy ? 'Saving…' : 'Got it'}</button>
          </div>
        </div>
      </div>
    )
  }

  if (showInstr) {
    return (
      <div style={S.scrim} role="dialog" aria-modal="true" aria-label="Instructions from the Operations Director">
        <div style={S.card} ref={cardRef}>
          <RoleChip role="Crew" what="A note from the Operations Director about what you're on" />
          <div style={S.eyebrow}>Triage · from the Operations Director</div>
          <h2 style={S.title}>{openInstr.every(a => a.kind === 'note') ? (openInstr.length === 1 ? 'A note about what you’re on.' : `${openInstr.length} notes about what you’re on.`) : openInstr.length === 1 ? 'One instruction about your work.' : `${openInstr.length} instructions about your work.`}</h2>
          <hr style={S.rule} />
          {openInstr.map(a => (
            <div key={a.id} style={S.ask}>
              <div style={S.askHead}>
                {a.flightNumber && <span style={S.askNo}>{a.flightNumber}</span>}
                <span style={S.askTitle}>{a.fromName || 'The Operations Director'} · {fmtDay(a.createdAt)}</span>
              </div>
              <div style={{ ...S.askNote, fontStyle: 'normal', fontSize: 14 }}>{a.text}</div>
              {a.milestoneId && <div style={{ marginTop: 2 }}>{openBtn(a, 'Open the milestone')}</div>}
              {tiles(a, instrOptions(a), setInstrs)}
            </div>
          ))}
          {err && <div style={S.err}>{err}</div>}
        </div>
        <EntityPeekModal target={peek} onClose={() => setPeek(null)} zIndex={1000005} />
      </div>
    )
  }

  if (!current && showAsks) {
    return (
      <div style={S.scrim} role="dialog" aria-modal="true" aria-label="Crews waiting on review or direction">
        <div style={S.card} ref={cardRef}>
          <RoleChip role="Design Director · Technical Director" what="Crews waiting on your review or direction" />
          <div style={S.eyebrow}>Triage · review and direction · {openAsks.length === 1 ? 'one ask' : `${openAsks.length} asks`}</div>
          <h2 style={S.title}>Crews are waiting on a review or direction.</h2>
          <div style={S.sub}>Most urgent first. Call each ball: take it, hand it to someone, answer it now, or send it back.</div>
          <ReviewWindow win={win} />
          <hr style={S.rule} />
          {openAsks.map(a => (
            <div key={a.id} style={{ ...S.ask, ...(a.calledToYou ? S.askCalled : null) }}>
              <div style={S.askHead}>
                {a.calledToYou
                  ? <span style={{ ...S.tag, ...S.tagCalled }}>Called to you</span>
                  : <span style={{ ...S.tag, ...(a.tag === 'finished' ? S.tagFinished : S.tagMid) }}>{a.tag === 'finished' ? 'Finished, waiting to post' : 'Mid-flight question'}</span>}
                <span style={S.askNo}>{a.flightNumber}</span>
                <span style={S.askTitle}>{a.projectName} · {a.milestoneName}</span>
              </div>
              <div style={S.askNote}>“{a.text}”</div>
              <div style={S.askMeta}>{a.createdBy || 'The captain'} · waiting {daysSince(a.createdAt)} day{daysSince(a.createdAt) === 1 ? '' : 's'}{a.crewCanWork === false ? ' · crew is stopped' : ''}{a.calledToYou && a.calledBy ? ` · ${a.calledBy} called it to you` : ''}{histLine(a)}</div>
              <div style={{ marginTop: 2 }}>{openBtn(a, 'Open the work')}</div>
              {tiles(a, askOptions(a), setAsks)}
              {a.calledToYou && <div style={S.hint}>Not taken today, it drops back for everyone tomorrow.</div>}
            </div>
          ))}
          {err && <div style={S.err}>{err}</div>}
          
        </div>
        <EntityPeekModal target={peek} onClose={() => setPeek(null)} zIndex={1000005} />
      </div>
    )
  }

  if (!current && showCoord) {
    return (
      <div style={S.scrim} role="dialog" aria-modal="true" aria-label="Crew requests to chase">
        <div style={S.card} ref={cardRef}>
          {/* The coordinator ROLE this card is for, by name (Matt, 2026-10-27): Client, Consultant, Permitting… */}
          <RoleChip role={[...new Set(openCoord.map(a => a.roleLabel).filter(Boolean))].join(' · ') || 'Coordinator'} what="Crews need something from outside the firm, and you chase it" />
          <div style={S.eyebrow}>Triage · requests to chase · {openCoord.length === 1 ? 'one' : openCoord.length}</div>
          <h2 style={S.title}>Crews need something from outside the firm.</h2>
          <div style={S.sub}>Read what each captain wrote, talk to them if it isn't clear, then say where it stands.</div>
          <hr style={S.rule} />
          {openCoord.map(a => {
            const captain = first(a.createdBy) || 'The captain'
            const canTalk = !!(a.createdById && a.createdById !== memberId)
            return (
            <div key={a.id} style={{ ...S.ask, ...(a.calledToYou ? S.askCalled : null) }}>
              <div style={S.askHead}>
                {a.calledToYou ? <span style={{ ...S.tag, ...S.tagCalled }}>Handed to you</span>
                  : a.overdue ? <span style={{ ...S.tag, ...S.tagFinished }}>Check back was {fmtDay(a.expectedDate)}</span>
                  : <span style={{ ...S.tag, ...S.tagMid }}>{(REQ_LABEL[a.category] || {}).label || a.category}</span>}
                <span style={S.askNo}>{a.flightNumber}</span>
                <span style={S.askTitle}>{a.projectName} · {a.milestoneName}</span>
              </div>
              <div style={S.askWho}>{a.createdBy || 'The captain'} wrote, {fmtDay(a.createdAt)}:</div>
              <div style={S.askNote}>“{a.text}”</div>
              <div style={S.askMeta}>{a.crewCanWork === false ? 'Their crew is stopped until this is handled' : 'Their crew can keep working meanwhile'}{histLine(a)}</div>
              {a.contextAskedAt && <div style={S.askMeta}>You talked to {captain} {fmtDay(a.contextAskedAt)}.</div>}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 2 }}>
                {canTalk && <button type="button" style={S.openLink} onClick={() => talkToCaptain(a)}><TileIcon name="chat" size={15} />Talk to {captain} in Slack</button>}
                {openBtn(a, 'Open the plane')}
              </div>
              {canTalk && !a.contextAskedAt && !a.calledToYou && <div style={S.hint}>Not sure what {captain} needs, or whether it's a request at all? Talk to them first.</div>}
              {tiles(a, coordOptions(a), setCoordReqs)}
              {a.calledToYou && <div style={S.hint}>Not taken today, it drops back to the usual coordinator tomorrow.</div>}
            </div>
            )
          })}
          {err && <div style={S.err}>{err}</div>}
          
        </div>
        <EntityPeekModal target={peek} onClose={() => setPeek(null)} zIndex={1000005} />
      </div>
    )
  }
  if (!current) return null

  // ── THE REPORT ────────────────────────────────────────────────────────────────────────────────
  const startHill = Math.round((current.hillPosition == null ? 0.08 : Number(current.hillPosition)) * 100)
  const moved = Math.abs(hill01 - startHill) >= 1
  const daysNum = Number(days)
  const carried = (current.requests || [])
  const openCarried = carried.filter(r => !r.closedAt && !gotIt[r.id] && !dropReq[r.id])
  const openCount = openCarried.length + newReqs.length
  const daysOk = keep === 'no' || (keep === 'yes' && days !== '' && Number.isFinite(daysNum) && daysNum > 0)
  const stoppedOnNothing = keep === 'no' && openCount === 0
  const noteNeeded = !moved && !note.trim()
  const selfChaseMissingDate = Object.values(selfChase).some(d => !d)
  const canSend = keep != null && daysOk && !stoppedOnNothing && !noteNeeded && !selfChaseMissingDate && !busy
  function addReq() {
    const t = draftText.trim()
    if (!t) { setReqErr('Say what you need first.'); return }
    const self = draftSelf && COORD_CATS.has(draftCat)
    if (self && !draftDate) { setReqErr('Pick the date you expect it, since you are chasing it.'); return }
    setNewReqs(r => [...r, { category: draftCat, text: t, ...(self ? { selfChase: true, expectedDate: draftDate } : {}) }]); setDraftText(''); setDraftSelf(false); setDraftDate(''); setReqErr('')
    setTimeout(() => draftInput.current && draftInput.current.focus(), 0)
  }
  const touring = tour != null

  async function reallySend() {
    setBusy(true); setErr('')
    try {
      const sent = await submitCaptainReport({
        memberId, memberName, flightId: current.flightId,
        hillPosition: hill01 / 100, daysRemaining: keep === 'no' ? 0 : daysNum, note: note.trim(),
        keepWorking: keep === 'yes', requests: newReqs,
        received: Object.keys(gotIt).filter(k => gotIt[k]), withdraw: Object.keys(dropReq).filter(k => dropReq[k]),
        resend: Object.entries(resendText).filter(([, t]) => String(t || '').trim()).map(([id, text]) => ({ id, text })),
        selfChase: Object.entries(selfChase).filter(([id, d]) => d && !gotIt[id] && !dropReq[id]).map(([id, expectedDate]) => ({ id, expectedDate })),
      })
      setConfirmNoMove(false)
      clearTriageDraft(memberId, 'captain-report', current.flightId).catch(() => {})
      clearLocalDraft(localDraftKey(memberId, current.flightId, today))
      // HOLDING (2026-10-22): stopped and waiting only on someone outside — the plane stepped aside.
      const h = sent && sent.holding
      if (h && h.parked) alertDialog({ title: `${current.flightNumber} is in holding`, message: `${h.reason}. It stays yours, but it no longer takes one of your two seats, so you can take other work. When what it waits for comes back, it returns to you on its own and is in your next morning report. The coordinator keeps chasing it meanwhile.` })
      else if (h && h.why) alertDialog({ title: `${current.flightNumber} keeps its seat`, message: `It can\u2019t step aside: ${h.why}.` })
      setResumedReport(false)
      setStatus(s => ({ ...s, flights: (s.flights || []).filter(f => f.flightId !== current.flightId) }))
    } catch (e) {
      setErr(String(e && e.message || e))
    } finally { setBusy(false) }
  }
  function send() {
    if (!canSend) return
    // The easy miss: every field filled, plane untouched. Sending that says "no progress today" to
    // the tower, so it has to be said on purpose. A waiting plane is expected not to move.
    if (!moved && keep !== 'no' && !confirmNoMove) { setConfirmNoMove(true); return }
    reallySend()
  }
  function endTour() {
    try { localStorage.setItem(TOUR_KEY, '1') } catch (_) { /* fine */ }
    setTour(null)
  }
  function tourNext() { if (tour >= TOUR_STEPS.length - 1) endTour(); else setTour(tour + 1) }
  function tourBack() { if (tour > 0) setTour(tour - 1) }

  // While the walkthrough runs, the step's target is lit and the rest of the form is faded and inert.
  // Only while its note is actually on screen: a walkthrough with no note must never leave the form inert.
  const spot = (key) => {
    if (!touring || !callout) return {}
    if (stepDef.target === key) return S.spotOn
    return S.spotOff
  }
  const ack = current.lastReport && current.lastReport.ack
  const postNow = (date, why) => window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: current.milestoneId, date, zIndex: 1000006, instruction: why } }))
  // Dragged to the end of the flight path (Matt, 2026-10-27: "I can't end it"): done is a POST, not a
  // days-left answer, so the same ask appears. The post window takes the real date, back-dated if need be.
  const atEnd = hill01 >= 95
  const askShown = current.milestoneId && !postAskDone[current.flightId] && (current.meetingAsk || current.nearDone || atEnd)

  return (
    <div style={S.scrim} role="dialog" aria-modal="true" aria-label="Captain's daily report">
      <div style={S.card} ref={cardRef}>
        <RoleChip role="Captain" what="You captain this plane: say where it stands" />
        <div style={{ ...S.eyebrow, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span>Captain's daily report · {due.length > 1 ? `1 of ${due.length}` : 'one milestone'}</span>
          {!touring && <button type="button" style={S.linkBtn} onClick={() => setTour(0)}>How this works</button>}
        </div>
        {current && current.backFromHoldingAt && (Date.now() - new Date(current.backFromHoldingAt).getTime()) < 4 * 86400000 && !resumedReport && <div style={{ fontSize: 13, color: '#1f7a4d', margin: '0 0 8px' }}>Back from holding {fmtDay(current.backFromHoldingAt)}: what it waited for came back. Where does it stand now?</div>}
        {resumedReport && <div style={{ fontSize: 13, color: '#1f7a4d', margin: '0 0 8px' }}>Picked up where you left off. Nothing is sent until you send the report.</div>}
        <div ref={refs.head} style={{ ...S.block, ...spot('head') }}>
          <h2 style={S.title}>{current.flightNumber} · {current.name || 'Milestone'}</h2>
          <div style={S.sub}>{current.projectName}{current.phaseName ? ` · ${current.phaseName}` : ''}{current.latestFinish ? ` · planned finish ${fmtDay(current.latestFinish)}` : ''}</div>
          {ack && (
            <div style={S.answer}>
              Tower answered your ask: {ack.kind === 'handled' ? <b>handled</b> : <b>{ack.by || 'the tower'} will do it {fmtDay(ack.date)}</b>}{ack.kind === 'handled' && ack.by ? ` by ${ack.by}` : ''}.
            </div>
          )}
        </div>
        {askShown && (
          // POST WHEN IT HAPPENS (2026-10-25): presented at the meeting, or done, posts now, dated; the post
          // window shows anything still in the way. Not yet: the report carries on.
          <div style={S.answer}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{current.meetingAsk ? `Was ${current.name || 'it'} presented at the ${fmtDay(current.meetingAsk.date)} meeting?` : `Is ${current.name || 'it'} done?`}</div>
            {postReady[current.milestoneId] && (postReady[current.milestoneId].openItems.length > 0 || postReady[current.milestoneId].unQc.length > 0) && (
              <div style={{ fontSize: 12.5, marginBottom: 6 }}>Before it can post: {[postReady[current.milestoneId].openItems.length && `${postReady[current.milestoneId].openItems.length} checklist item${postReady[current.milestoneId].openItems.length === 1 ? '' : 's'} open`, postReady[current.milestoneId].unQc.length && `${postReady[current.milestoneId].unQc.length} sheet${postReady[current.milestoneId].unQc.length === 1 ? '' : 's'} not through QC`].filter(Boolean).join(' · ')}.</div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={S.openLink} onClick={() => { setPostAskDone(d => ({ ...d, [current.flightId]: true })); postNow(current.meetingAsk ? current.meetingAsk.date : todayYmd(), current.meetingAsk ? `Presented at the ${fmtDay(current.meetingAsk.date)} meeting.` : 'Done, from the captain’s report.') }}>Yes, post it</button>
              <button type="button" style={S.openLink} onClick={() => setPostAskDone(d => ({ ...d, [current.flightId]: true }))}>Not yet</button>
            </div>
          </div>
        )}
        <hr style={S.rule} />

        <div ref={refs.hill} style={{ ...S.field, ...spot('hill') }}>
          <div style={S.labelRow}>
            <label style={S.label}>Where is it on the flight path?</label>
            <span style={S.reading}>{hillRead(hill01 / 100)} · {hill01}%{!moved ? ' · same spot as before' : ''}</span>
          </div>
          <FlightPathSlider value={hill01} onChange={v => { setHill01(v); setConfirmNoMove(false) }} disabled={busy} />
        </div>

        {/* 2 · WHAT ARE YOU STILL WAITING TO RECEIVE (2026-10-15): any number of requests, any mix. */}
        <div ref={refs.blocker} style={{ ...S.field, ...spot('blocker') }}>
          <label style={S.label}>What are you still waiting to receive?</label>
          <div style={{ ...S.hint, marginTop: -2 }}>Only what you don't have yet. Ask early, even with other work to do: it reaches someone's queue sooner.</div>
          {carried.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {carried.map(r => {
                const closed = !!r.closedAt
                const sentBack = r.status === 'sent_back'
                return (
                  <div key={r.id} style={{ ...S.reqRow, ...(gotIt[r.id] || closed ? S.reqDone : null) }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.reqText}>{r.text}</div>
                      <div style={{ ...S.reqMeta, ...(sentBack ? { color: '#b8540a' } : null) }}>{(REQ_LABEL[r.category] || {}).label || r.category} · {gotIt[r.id] ? 'Received. Clears it and tells whoever was chasing.' : dropReq[r.id] ? 'Withdrawn.' : reqStatus(r)}</div>
                      {selfChase[r.id] !== undefined && !gotIt[r.id] && (
                        <label style={{ ...S.hint, display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>You're chasing it. Expected
                          <input type="date" value={selfChase[r.id]} onChange={e => setSelfChase(x => ({ ...x, [r.id]: e.target.value }))} style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: 12 }} />
                        </label>
                      )}
                      {sentBack && !dropReq[r.id] && (
                        <input value={resendText[r.id] ?? r.text} onChange={e => setResendText(x => ({ ...x, [r.id]: e.target.value }))} style={{ ...S.input, fontFamily: '"DM Sans", sans-serif', fontSize: 13, marginTop: 6 }} placeholder="Clarify it and it goes back out" />
                      )}
                    </div>
                    {!closed && !sentBack && !gotIt[r.id] && r.route === 'coordinator' && r.status === 'open' && (
                      <button type="button" style={S.smallBtn} onClick={() => setSelfChase(x => { const y = { ...x }; if (y[r.id] !== undefined) delete y[r.id]; else y[r.id] = ''; return y })}>{selfChase[r.id] !== undefined ? 'Undo' : "I'm chasing it"}</button>
                    )}
                    {!closed && !sentBack && <button type="button" style={S.smallBtn} onClick={() => setGotIt(g => ({ ...g, [r.id]: !g[r.id] }))}>{gotIt[r.id] ? 'Undo' : 'We got it'}</button>}
                    {sentBack && <button type="button" style={S.smallBtn} onClick={() => setDropReq(g => ({ ...g, [r.id]: !g[r.id] }))}>{dropReq[r.id] ? 'Undo' : 'Withdraw'}</button>}
                  </div>
                )
              })}
            </div>
          )}
          {newReqs.map((r, i) => (
            <div key={'n' + i} style={{ ...S.reqRow, ...S.reqNew }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.reqText}>{r.text}</div>
                <div style={S.reqMeta}>
                  {REQ_LABEL[r.category].label} · {r.selfChase ? `you're chasing it, expected ${fmtDay(r.expectedDate)}` : REQ_LABEL[r.category].who}
                  <span style={S.reqNewTag}> · new, goes out when you send the report</span>
                </div>
              </div>
              <button type="button" style={S.linkSmall} onClick={() => setNewReqs(x => x.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          {carried.length === 0 && newReqs.length === 0 && <div style={S.reqEmpty}>Nothing on the list. Add anything you need and don't have yet.</div>}
          <div style={S.addBox}>
            <input ref={draftInput} value={draftText} onChange={e => { setDraftText(e.target.value); setReqErr('') }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addReq() } }}
              placeholder="What do you need? e.g. island or peninsula in the kitchen" style={{ ...S.input, fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, padding: '9px 11px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <span style={S.fromLbl}>From</span>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <Select value={draftCat} onChange={v => { setDraftCat(v); setReqErr(''); if (!COORD_CATS.has(v)) setDraftSelf(false) }} options={REQ_CATS.map(c => ({ value: c.value, label: c.pick }))} style={{ width: '100%', fontSize: 13 }} />
              </div>
              <button type="button" style={{ ...S.primary, opacity: draftText.trim() ? 1 : 0.4 }} disabled={!draftText.trim()} onClick={addReq}>Add to the list</button>
            </div>
            {COORD_CATS.has(draftCat) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <label style={S.checkRow}>
                  <input type="checkbox" checked={draftSelf} onChange={e => { setDraftSelf(e.target.checked); setReqErr('') }} />
                  I already asked them. I'll chase it myself
                </label>
                {draftSelf
                  ? <label style={{ ...S.hint, display: 'flex', alignItems: 'center', gap: 6 }}>expected
                      <input type="date" value={draftDate} onChange={e => { setDraftDate(e.target.value); setReqErr('') }} style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: 12 }} /></label>
                  : <span style={S.hint}>Otherwise {REQ_LABEL[draftCat].who}.</span>}
              </div>
            ) : <div style={{ ...S.hint, marginTop: 8 }}>{REQ_LABEL[draftCat].who.replace(/^the/, 'The')}.</div>}
            {reqErr && <div style={{ ...S.err, margin: '6px 0 0' }}>{reqErr}</div>}
          </div>
        </div>

        {/* 3 · CAN YOU KEEP WORKING (a separate question from what you are waiting for). */}
        <div ref={refs.days} style={{ ...S.field, ...spot('days') }}>
          <label style={S.label}>Can you keep working right now?</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setKeep('yes')} style={{ ...S.choice, ...(keep === 'yes' ? S.choiceOn : null) }}>
              <b>Yes</b><span style={S.choiceSub}>We have our own work to do</span>
            </button>
            <button type="button" onClick={() => { setKeep('no'); setDays('') }} style={{ ...S.choice, ...(keep === 'no' ? S.choiceOn : null) }}>
              <b>No, we're stopped</b><span style={S.choiceSub}>Nothing we can do until someone else acts</span>
            </button>
          </div>
          {keep === 'yes' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <span style={S.hint}>Days of your crew's own hands-on work left. Never count waiting time.</span>
              <input type="number" min="0.5" step="0.5" inputMode="decimal" value={days} onChange={e => setDays(e.target.value)} placeholder="e.g. 3" style={{ ...S.input, width: 90 }} />
            </div>
          )}
          {keep === 'yes' && days !== '' && !(daysNum > 0) && <div style={{ ...S.hint, color: '#b8540a' }}>{atEnd ? 'Zero days left means it is done: use "Yes, post it" at the top, on the day it really finished.' : "Zero days of your own work left? If it's done, drag it to the end of the flight path and post it. If you're waiting, pick No."}</div>}
          {stoppedOnNothing && <div style={{ ...S.hint, color: '#b8540a' }}>Stopped on what? Add what you're still waiting to receive above.</div>}
          {keep === 'no' && <div style={S.hint}>The tower sees a stopped plane, so it isn't left holding your seat.</div>}
        </div>

        <div ref={refs.note} style={{ ...S.field, ...spot('note') }}>
          <label style={S.label}>One line for the tower{noteNeeded ? <span style={S.req}> · required when the plane did not move</span> : <span style={S.opt}> · optional</span>}</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={waiting ? 'Who you are waiting on and for what' : (moved ? 'What changed, in a sentence' : 'Why it sits where it sits today')} style={S.textarea} />
        </div>

        {err && <div style={S.err}>{err}</div>}

        {confirmNoMove && !touring ? (
          <div style={S.confirm}>
            <div style={S.confirmTitle}>You did not move the plane. Is that right?</div>
            <div style={S.confirmBody}>
              Sending it as is tells the tower there was no progress on {current.flightNumber} today. If you did make progress, go back and drag the plane to where the work stands now.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" style={S.ghost} onClick={() => setConfirmNoMove(false)}>Go back and move it</button>
              <button type="button" style={{ ...S.primary, opacity: busy ? 0.45 : 1 }} disabled={busy} onClick={reallySend}>{busy ? 'Sending…' : 'Yes, no progress today. Send.'}</button>
            </div>
          </div>
        ) : (
          <div ref={refs.foot} style={{ ...S.foot, ...spot('foot') }}>
            <span style={S.footNote}>Only the captain answers. This moves the milestone on the Flight Board, feeds tonight's schedule, and posts to the project's Slack channel.</span>
            <button type="button" style={{ ...S.primary, opacity: canSend ? 1 : 0.45 }} disabled={!canSend} onClick={send}>{busy ? 'Sending…' : 'Send report'}</button>
          </div>
        )}
      </div>

      {touring && callout && (
        <div style={{ ...S.callout, width: callout.width, left: callout.left, top: callout.above ? undefined : callout.top, bottom: callout.above ? (window.innerHeight - callout.top) : undefined }} role="note">
          <div style={S.calloutStep}>Step {tour + 1} of {TOUR_STEPS.length}</div>
          <div style={S.calloutTitle}>{stepDef.title}</div>
          <div style={S.calloutBody}>{stepDef.body}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <button type="button" style={S.calloutSkip} onClick={endTour}>Skip</button>
            <div style={{ display: 'flex', gap: 8 }}>
              {tour > 0 && <button type="button" style={S.ghost} onClick={tourBack}>Back</button>}
              <button type="button" style={S.primary} onClick={tourNext}>{tour >= TOUR_STEPS.length - 1 ? 'Done, fill it in' : 'Next'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const S = {
  scrim: { position: 'fixed', inset: 0, zIndex: 1000000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(4,21,24,0.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' },
  card: { width: '100%', maxWidth: 620, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto', background: 'rgba(255,255,255,0.97)', border: '0.5px solid rgba(255,255,255,0.6)', borderRadius: 16, boxShadow: '0 24px 64px rgba(4,21,24,0.5)', padding: 'clamp(22px, 3.5vw, 36px)', display: 'flex', flexDirection: 'column' },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#b8540a', marginBottom: 10 },
  block: { borderRadius: 6 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 400, letterSpacing: '0.02em', color: '#07272D', lineHeight: 1.3, margin: 0 },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#5b8494', marginTop: 4 },
  answer: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#07272D', marginTop: 8, padding: '7px 10px', borderRadius: 6, background: 'rgba(7,39,45,0.05)', border: '0.5px solid rgba(7,39,45,0.12)' },
  rule: { height: '0.5px', background: 'rgba(7,39,45,0.12)', border: 'none', margin: '14px 0', width: 60 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, flex: '1 1 0', minWidth: 0, borderRadius: 6 },
  twoUp: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  label: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#07272D' },
  reading: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: '#3a5260' },
  input: { font: 'inherit', fontFamily: '"DM Mono", monospace', fontSize: 14, padding: '7px 10px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', width: '100%', boxSizing: 'border-box' },
  waitRow: { display: 'flex', alignItems: 'center', gap: 7, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#07272D', cursor: 'pointer', marginTop: 2 },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#8aa6b3', lineHeight: 1.45 },
  textarea: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', width: '100%', boxSizing: 'border-box', resize: 'vertical' },
  req: { fontWeight: 400, color: '#b8540a' },
  opt: { fontWeight: 400, color: '#8aa6b3' },
  err: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#c0392b', marginBottom: 10 },
  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4, borderRadius: 6 },
  footNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#5b8494', flex: '1 1 240px' },
  ghost: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, padding: '8px 14px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', cursor: 'pointer' },
  primary: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, padding: '8px 16px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
  linkBtn: { font: 'inherit', fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#5b8494', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  // The no-movement confirm, in the footer's place.
  confirm: { border: '0.5px solid rgba(184,84,10,0.45)', background: 'rgba(184,84,10,0.06)', borderRadius: 8, padding: '12px 14px', marginTop: 4 },
  confirmTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: '#07272D', marginBottom: 4 },
  confirmBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, lineHeight: 1.5, color: '#3a5260', marginBottom: 12 },
  // Asks for the tower.
  ask: { padding: '14px 0 16px', borderTop: '0.5px solid rgba(7,39,45,0.12)' },
  askHead: { display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' },
  askNo: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: '#07272D' },
  askTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#07272D', flex: '1 1 auto', minWidth: 0 },
  askMeta: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#8aa6b3' },
  askWho: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#5b8494', marginTop: 8 },
  askNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, lineHeight: 1.5, color: '#3a5260', fontStyle: 'italic', margin: '6px 0 8px' },
  askActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  askOr: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#8aa6b3' },
  dateInput: { font: 'inherit', fontFamily: '"DM Mono", monospace', fontSize: 12.5, padding: '6px 8px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D' },
  openLink: { font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#07272D', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  // The review window (2026-10-16)
  win: { display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(7,39,45,0.04)', border: '0.5px solid rgba(7,39,45,0.12)', fontFamily: '"DM Sans", sans-serif', fontSize: 12 },
  winCol: { flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  winHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#5b8494', marginBottom: 2 },
  winLine: { color: '#07272D', lineHeight: 1.45 },
  // Requests (2026-10-15)
  reqRow: { display: 'flex', alignItems: 'center', gap: 10, border: '0.5px solid rgba(7,39,45,0.15)', borderLeft: '3px solid rgba(7,39,45,0.35)', borderRadius: 6, padding: '8px 10px', background: '#fff' },
  reqNew: { borderLeftColor: '#b8540a', background: 'rgba(184,84,10,0.04)' },
  reqNewTag: { color: '#b8540a' },
  reqMeta: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#5b8494', marginTop: 2, lineHeight: 1.45 },
  reqEmpty: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#8aa6b3', fontStyle: 'italic' },
  fromLbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#3a5260' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#07272D', cursor: 'pointer' },
  linkSmall: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#5b8494', background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, flexShrink: 0 },
  reqDone: { background: 'rgba(29,158,117,0.08)', borderLeftColor: '#1d9e75' },
  reqText: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D' },
  reqCat: { color: '#5b8494' },
  addBox: { border: '0.5px solid rgba(7,39,45,0.2)', borderRadius: 8, padding: '10px 12px', background: 'rgba(7,39,45,0.025)', marginTop: 2 },
  chip: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '5px 10px', borderRadius: 20, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', cursor: 'pointer' },
  chipOn: { background: '#07272D', color: '#fff', borderColor: '#07272D' },
  smallBtn: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '5px 10px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', cursor: 'pointer', flexShrink: 0 },
  choice: { font: 'inherit', flex: '1 1 200px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 13 },
  choiceOn: { border: '1.5px solid #07272D', background: 'rgba(7,39,45,0.04)' },
  choiceSub: { fontSize: 11.5, color: '#5b8494' },
  tag: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, padding: '2px 8px', borderRadius: 4 },
  tagFinished: { background: 'rgba(184,84,10,0.12)', color: '#8a3f06' },
  tagMid: { background: 'rgba(7,39,45,0.07)', color: '#3a5260' },
  tagCalled: { background: 'rgba(29,111,184,0.12)', color: '#164f86' },
  askCalled: { border: '1px solid rgba(29,111,184,0.45)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 },
  select: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, padding: '7px 8px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', color: '#07272D', maxWidth: 190 },
  // Walkthrough: the lit target and the faded rest.
  spotOn: { boxShadow: '0 0 0 2px #b8540a, 0 0 0 7px rgba(184,84,10,0.14)', position: 'relative', zIndex: 2, background: '#fff' },
  spotOff: { opacity: 0.28, pointerEvents: 'none', filter: 'grayscale(0.4)' },
  callout: { position: 'fixed', zIndex: 1000001, background: '#07272D', color: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 16px 40px rgba(4,21,24,0.45)' },
  calloutStep: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#f0b27a', marginBottom: 6 },
  calloutTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: 1.35, marginBottom: 6 },
  calloutBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' },
  calloutSkip: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
}
